import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../api/client";
import { isSuperAdmin } from "../utils/permissions";
import ProjectNav from "./ProjectNav";

const DEPARTMENTS = ["Finance", "Development", "CAM", "Engineering", "Program Management", "Operations"];

export default function ProjectMembersView() {
  const { projectId } = useParams();
  const [projectName, setProjectName] = useState("");
  const [members, setMembers] = useState<any[]>([]);
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [accessRequests, setAccessRequests] = useState<any[]>([]);
  const [showAdd, setShowAdd] = useState(false);
  const [addMode, setAddMode] = useState<"existing" | "new">("existing");
  const [selectedExisting, setSelectedExisting] = useState<{ user_id: string; role: string } | null>(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", department: "", role: "viewer" });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const currentUserId = localStorage.getItem("user_id");

  useEffect(() => {
    if (!projectId) return;
    api.get("/api/projects").then(r => {
      const proj = r.data.find((p: any) => p.id === projectId);
      if (proj) setProjectName(proj.name);
    }).catch(() => {});
    fetchAll();
  }, [projectId]);

  const fetchAll = () => {
    api.get(`/api/projects/${projectId}/members`).then(r => setMembers(r.data)).catch(() => setMembers([]));
    api.get(`/api/users/list-for-project/${projectId}`).then(r => setAllUsers(r.data)).catch(() => {});
    api.get("/api/access-requests").then(r => {
      setAccessRequests(r.data.filter((req: any) => 
        req.project_id === projectId && 
        req.status === "pending" &&
        !req.note?.startsWith("Added by project admin")
      ));
    }).catch(() => {});
  };

  const nonMembers = allUsers.filter(u => !members.find(m => m.user_id === u.id));

  const handleAddExisting = async () => {
    if (!selectedExisting) { setError("Select a user"); return; }
    setLoading(true); setError("");
    try {
      await api.post(`/api/projects/${projectId}/members`, {
        user_id: selectedExisting.user_id,
        role: selectedExisting.role,
      });
      setShowAdd(false);
      setSelectedExisting(null);
      fetchAll();
    } catch (e: any) {
      setError(e.response?.data?.detail || "Failed to add member");
    } finally { setLoading(false); }
  };

  const handleAddNew = async () => {
    if (!form.name || !form.email || !form.password) { setError("Name, email, and password required"); return; }
    setLoading(true); setError("");
    try {
      // Register new user as inactive — super admin must activate
      await api.post("/api/auth/register", {
        name: form.name,
        email: form.email,
        password: form.password,
        department: form.department,
        requested_project_id: projectId,
        requested_role: form.role,
      });
      setForm({ name: "", email: "", password: "", department: "", role: "viewer" });
      setShowAdd(false);
      setError("");
      alert("User registration submitted. Super admin must approve the account before they can access this project.");
      fetchAll();
    } catch (e: any) {
      setError(e.response?.data?.detail || "Failed to add member");
    } finally { setLoading(false); }
  };

  const handleRoleChange = async (userId: string, role: string) => {
    try {
      await api.put(`/api/projects/${projectId}/members/${userId}`, { role });
      fetchMembers();
    } catch {}
  };

  const fetchMembers = () => {
    api.get(`/api/projects/${projectId}/members`).then(r => setMembers(r.data)).catch(() => {});
  };

  const handleRemove = async (userId: string) => {
    if (!window.confirm("Remove this member from the project?")) return;
    try {
      await api.delete(`/api/projects/${projectId}/members/${userId}`);
      fetchAll();
    } catch {}
  };

  const handleApproveRequest = async (reqId: string) => {
    try { await api.put(`/api/access-requests/${reqId}/approve`); fetchAll(); } catch {}
  };

  const handleRejectRequest = async (reqId: string) => {
    try { await api.put(`/api/access-requests/${reqId}/reject`); fetchAll(); } catch {}
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <ProjectNav projectId={projectId!} active="members" />

      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="flex items-start justify-between mb-5">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">PROJECT MEMBERS</h1>
            <p className="text-xs text-gray-400 mt-0.5">
              {projectName ? `Manage who has access to ${projectName}.` : "Manage project access."}
            </p>
          </div>
          <button onClick={() => { setShowAdd(true); setError(""); setAddMode("existing"); setSelectedExisting(null); }}
            className="flex items-center gap-2 bg-blue-600 text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-blue-700">
            + Add Member
          </button>
        </div>

        {/* Pending access requests — shown to super_admin only */}
        {accessRequests.length > 0 && (
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-4">
            <h3 className="text-sm font-semibold text-orange-800 mb-3">Pending Access Requests ({accessRequests.length})</h3>
            <div className="space-y-2">
              {accessRequests.map(req => (
                <div key={req.id} className="flex items-center justify-between bg-white rounded-lg px-4 py-2.5 border border-orange-100">
                  <div>
                    <span className="text-sm font-medium text-gray-900">{req.user_name}</span>
                    <span className="text-xs text-gray-400 ml-2">{req.user_email}</span>
                    <span className="ml-2 text-xs px-2 py-0.5 bg-blue-50 text-blue-600 rounded border border-blue-100">{req.requested_role}</span>
                    {req.note && <p className="text-xs text-gray-400 mt-0.5">Note: {req.note}</p>}
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => handleApproveRequest(req.id)}
                      className="px-3 py-1 bg-green-100 text-green-700 rounded text-xs font-medium hover:bg-green-200">Approve</button>
                    <button onClick={() => handleRejectRequest(req.id)}
                      className="px-3 py-1 bg-red-100 text-red-700 rounded text-xs font-medium hover:bg-red-200">Reject</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Members table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {["Name", "Email", "Department", "Project Role", "Actions"].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {members.map((m: any) => {
                const isSelf = m.user_id === currentUserId;
                return (
                  <tr key={m.user_id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {m.name} {isSelf && <span className="text-xs text-gray-400">(you)</span>}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">{m.email}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{m.department || "—"}</td>
                    <td className="px-4 py-3">
                      {isSelf ? (
                        <span className="text-xs text-gray-500 px-2 py-1 bg-gray-100 rounded">
                          {m.role?.charAt(0).toUpperCase() + m.role?.slice(1)}
                        </span>
                      ) : (
                        <select value={m.role} onChange={e => handleRoleChange(m.user_id, e.target.value)}
                          className="text-xs border border-gray-200 rounded px-2 py-1 text-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-500">
                          <option value="viewer">Viewer</option>
                          <option value="editor">Editor</option>
                          <option value="admin">Admin</option>
                        </select>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {!isSelf && (
                        <button onClick={() => handleRemove(m.user_id)}
                          className="text-xs text-red-500 hover:text-red-700 font-medium px-2 py-1 rounded hover:bg-red-50">
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {members.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400 text-sm">No members yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Member Modal */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl border border-gray-200 p-6 w-full max-w-md shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Add Member</h2>
                <p className="text-xs text-gray-400 mt-0.5">Add an existing user or register a new one.</p>
              </div>
              <button onClick={() => { setShowAdd(false); setError(""); }} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>

            {/* Mode toggle */}
            <div className="flex gap-2 mb-4">
              <button onClick={() => setAddMode("existing")}
                className={`flex-1 py-1.5 rounded-lg text-sm font-medium border ${addMode === "existing" ? "bg-blue-600 text-white border-blue-600" : "border-gray-200 text-gray-600"}`}>
                Existing User
              </button>
              <button onClick={() => setAddMode("new")}
                className={`flex-1 py-1.5 rounded-lg text-sm font-medium border ${addMode === "new" ? "bg-blue-600 text-white border-blue-600" : "border-gray-200 text-gray-600"}`}>
                New User
              </button>
            </div>

            {addMode === "existing" ? (
              <div className="space-y-3">
                <p className="text-xs text-gray-500">Select an existing user to add to this project:</p>
                <div className="border border-gray-200 rounded-lg max-h-52 overflow-y-auto">
                  {nonMembers.length === 0 && (
                    <p className="text-xs text-gray-400 p-4 text-center">All users are already members</p>
                  )}
                  {nonMembers.map(u => (
                    <div key={u.id}
                      onClick={() => setSelectedExisting(selectedExisting?.user_id === u.id ? null : { user_id: u.id, role: "viewer" })}
                      className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-gray-50 border-b border-gray-50 last:border-0
                        ${selectedExisting?.user_id === u.id ? "bg-blue-50" : ""}`}>
                      <input type="radio" readOnly checked={selectedExisting?.user_id === u.id} className="w-4 h-4" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{u.name}</p>
                        <p className="text-xs text-gray-400">{u.email}</p>
                      </div>
                      {u.department && <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{u.department}</span>}
                    </div>
                  ))}
                </div>
                {selectedExisting && (
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Project Role</label>
                    <select value={selectedExisting.role}
                      onChange={e => setSelectedExisting({ ...selectedExisting, role: e.target.value })}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="viewer">Viewer</option>
                      <option value="editor">Editor</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                )}
                {error && <p className="text-red-500 text-xs">{error}</p>}
                <div className="flex gap-2 mt-4">
                  <button onClick={() => { setShowAdd(false); setError(""); }}
                    className="flex-1 border border-gray-300 rounded-lg py-2 text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
                  <button onClick={handleAddExisting} disabled={loading || !selectedExisting}
                    className="flex-1 bg-blue-600 text-white rounded-lg py-2 text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                    {loading ? "Adding..." : "Add Member"}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {!isSuperAdmin() && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg px-3 py-2 text-xs text-orange-700">
                    New users added here will need super admin approval before getting project access.
                  </div>
                )}
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Full Name *</label>
                  <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Email Address *</label>
                  <input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Password *</label>
                  <div className="relative">
                    <input type={showPassword ? "text" : "password"} value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    <button type="button" onClick={() => setShowPassword(p => !p)} className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                      {showPassword ? "Hide" : "Show"}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Department</label>
                  <select value={form.department} onChange={e => setForm(f => ({ ...f, department: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="">Select Department</option>
                    {DEPARTMENTS.map(d => <option key={d}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Project Role</label>
                  <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="viewer">Viewer</option>
                    <option value="editor">Editor</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                {error && <p className="text-red-500 text-xs">{error}</p>}
                <div className="flex gap-2 mt-4">
                  <button onClick={() => { setShowAdd(false); setError(""); }}
                    className="flex-1 border border-gray-300 rounded-lg py-2 text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
                  <button onClick={handleAddNew} disabled={loading}
                    className="flex-1 bg-blue-600 text-white rounded-lg py-2 text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                    {loading ? "Submitting..." : isSuperAdmin() ? "Add Member" : "Submit for Approval"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
